import { Injectable } from '@angular/core';
import { Effect, Actions, ofType } from '@ngrx/effects';
import { Observable, of, Subscription, zip } from 'rxjs';
import {
  switchMap,
  map,
  catchError,
  tap,
  mergeMap,
  exhaustMap,
  first
} from 'rxjs/operators';
import { AlertService, AlertType, CupcakeFlavors } from '@ipreo/ngx-sprinkles';
import { FollowsService } from '../../services/follows.service';
import {
  FollowsActionTypes,
  LoadSuccess,
  LoadFail,
  Follow,
  FollowFail,
  Unfollow,
  UnfollowFail,
  UnfollowMultiple,
  FollowMultiple,
  GroupFollowFail,
  GroupFollow,
  GroupUnfollowFail,
  GroupUnfollow,
  FollowSuccess,
  GroupFollowSuccess,
  FollowMultipleToggleSuccess,
  UnfollowSuccess,
  GroupUnfollowSuccess
} from './actions';
import { PermissionsFacade } from '../../facades/permissions.facade';

@Injectable()
export class FollowsEffects {
  public groupFollowSubscribtion$: Subscription;

  @Effect()
  public loadAllFollows$: Observable<
    LoadSuccess | LoadFail
  > = this.actions$.pipe(
    ofType(FollowsActionTypes.Load),
    exhaustMap(() =>
      this.permissionsFacade.permissions$.pipe(
        first(),
        switchMap((permissions) =>
          zip(
            permissions.following
              ? this.followsService.getFollows()
              : of([] as string[]),
            permissions.groupFollowing
              ? this.followsService.getGroupFollows()
              : of([] as string[])
          ).pipe(
            map(
              ([follows, groupFollows]) =>
                new LoadSuccess({ follows, groupFollows })
            ),
            catchError((err: Error) => of(new LoadFail(err && err.message)))
          )
        )
      )
    )
  );

  @Effect()
  public followDeal$: Observable<
    FollowSuccess | FollowFail
  > = this.actions$.pipe(
    ofType(FollowsActionTypes.Follow),
    mergeMap((action: Follow) => {
      return this.followsService.follow(action.payload).pipe(
        map(() => new FollowSuccess({ dealId: action.payload })),
        catchError((err: Error) =>
          of(
            new FollowFail({
              errorMessage: err && err.message,
              dealIds: [action.payload]
            })
          )
        )
      );
    })
  );

  @Effect()
  public unfollowDeal$: Observable<
    UnfollowSuccess | UnfollowFail
  > = this.actions$.pipe(
    ofType(FollowsActionTypes.Unfollow),
    mergeMap((action: Unfollow) => {
      return this.followsService.unfollow(action.payload).pipe(
        map(() => new UnfollowSuccess({ dealId: action.payload })),
        catchError((err: Error) =>
          of(
            new UnfollowFail({
              errorMessage: err && err.message,
              dealIds: [action.payload]
            })
          )
        )
      );
    })
  );

  @Effect()
  public followMultipleDeals$: Observable<
    FollowMultipleToggleSuccess | FollowFail
  > = this.actions$.pipe(
    ofType(FollowsActionTypes.FollowMultiple),
    switchMap((action: FollowMultiple) => {
      return this.followsService.followMultiple(action.payload).pipe(
        map(
          (dealIds: string[]) =>
            new FollowMultipleToggleSuccess({
              deals: dealIds,
              dealIdsFollowInProcess: action.payload
            })
        ),
        catchError((err: Error) =>
          of(
            new FollowFail({
              errorMessage: err && err.message,
              dealIds: action.payload
            })
          )
        )
      );
    })
  );

  @Effect()
  public unfollowMultipleDeals$: Observable<
    FollowMultipleToggleSuccess | UnfollowFail
  > = this.actions$.pipe(
    ofType(FollowsActionTypes.UnfollowMultiple),
    switchMap((action: UnfollowMultiple) => {
      return this.followsService.unfollowMultiple(action.payload).pipe(
        map(
          (dealIds: string[]) =>
            new FollowMultipleToggleSuccess({
              deals: dealIds,
              dealIdsFollowInProcess: action.payload
            })
        ),
        catchError((err: Error) =>
          of(
            new UnfollowFail({
              errorMessage: err && err.message,
              dealIds: action.payload
            })
          )
        )
      );
    })
  );

  @Effect()
  public groupFollowDeal$: Observable<
    GroupFollowSuccess | GroupFollowFail
  > = this.actions$.pipe(
    ofType(FollowsActionTypes.GroupFollow),
    mergeMap((action: GroupFollow) => {
      return this.followsService.groupFollow(action.payload.dealId).pipe(
        tap(() => {
          if (action.payload.notify) {
            this.alertService.create({
              body: 'Group follow has been enabled for this deal',
              alertType: AlertType.toast,
              color: CupcakeFlavors.Success,
              icon: 'fa fa-check-circle',
              timeout: 8000
            });
          }
        }),
        map(() => new GroupFollowSuccess({ dealId: action.payload.dealId })),
        catchError((err: Error) =>
          of(
            new GroupFollowFail({
              errorMessage: err && err.message,
              dealId: action.payload.dealId
            })
          )
        )
      );
    })
  );

  @Effect()
  public groupUnfollowDeal$: Observable<
    GroupUnfollowSuccess | GroupUnfollowFail
  > = this.actions$.pipe(
    ofType(FollowsActionTypes.GroupUnfollow),
    mergeMap((action: GroupUnfollow) => {
      return this.followsService.groupUnfollow(action.payload).pipe(
        map(
          () =>
            new GroupUnfollowSuccess({
              dealId: action.payload
            })
        ),
        catchError((err: Error) =>
          of(
            new GroupUnfollowFail({
              errorMessage: err && err.message,
              dealId: action.payload
            })
          )
        )
      );
    })
  );
  webSocketGatewayService: any;

  constructor(
    private actions$: Actions,
    private followsService: FollowsService,
    private permissionsFacade: PermissionsFacade,
    private alertService: AlertService
  ) {}
}
